import React from 'react';
import { Box, Button, ButtonProps } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

/**
 * Mirrors the Figma "Button" component (Dosen 3.0, node 2078:5588).
 *
 * Figma → component prop mapping
 *  Type        → tone:    'cta' | 'basic' (Secondary) | 'options' (Default)
 *  Size        → sizeVariant: 'xs' | 'sm' | 'md' (alias for sm) | 'lg' (Figma "Default") | 'xl' (Figma "Large")
 *  State       → derived from `disabled`/`loading`/MUI hover
 *  Style       → styleVariant: 'filled' | 'outline' (outline only valid for tone='cta')
 *  Icon Place  → iconPosition: 'start' | 'end' (and absent when no `icon`)
 *
 * House rules (see ./buttonStyles.ts): text buttons carry no icon, so `icon` is
 * only drawn on an `iconOnly` button; disabled is dark with dim text; while
 * `loading`, the label switches to `loadingText` ("Saving...") instead of a
 * spinner.
 */

export type DosenButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type IconPosition = 'start' | 'end';
export type DosenButtonTone = 'cta' | 'basic' | 'options';
export type DosenButtonStyle = 'filled' | 'outline';

interface DosenButtonProps extends Omit<ButtonProps, 'color' | 'variant'> {
  sizeVariant?: DosenButtonSize;
  iconPosition?: IconPosition;
  icon?: React.ReactNode;
  tone?: DosenButtonTone;
  styleVariant?: DosenButtonStyle;
  iconOnly?: boolean;
  locked?: boolean;
  loading?: boolean;
  // Label shown while `loading`, e.g. "Saving...". Falls back to the children.
  loadingText?: React.ReactNode;
}

type SizeKey = 'xs' | 'sm' | 'lg' | 'xl';

// `md` is treated as a `sm` alias to keep older callers working.
const sizeAlias = (s: DosenButtonSize): SizeKey => (s === 'md' ? 'sm' : s);

const paddingMap: Record<SizeKey, { start: string; end: string; none: string }> = {
  xs: {
    start: '4px 10px 4px 8px',
    end: '4px 8px 4px 10px',
    none: '4px 10px'
  },
  sm: {
    start: '10px 14px 10px 12px',
    end: '10px 12px 10px 14px',
    none: '10px 14px'
  },
  lg: {
    start: '14px 16px 14px 14px',
    end: '14px 14px 14px 16px',
    none: '14px 16px'
  },
  xl: {
    start: '17px 24px 17px 21px',
    end: '17px 21px 17px 24px',
    none: '17px 24px'
  }
};

const fontMap: Record<SizeKey, { fontSize: number; lineHeight: number; borderRadius: number; gap: number }> = {
  xs: { fontSize: 12, lineHeight: 1.7, borderRadius: 4, gap: 5 },
  sm: { fontSize: 13, lineHeight: 1.5, borderRadius: 6, gap: 5 },
  lg: { fontSize: 14, lineHeight: 1.7, borderRadius: 6, gap: 5 },
  xl: { fontSize: 18, lineHeight: 1.7, borderRadius: 7, gap: 10 }
};

// --- Token references (kept as `var(...)` so a runtime theme swap is possible). ---
const tone_cta_bg = 'var(--gradient-cta)';
const tone_cta_text = 'var(--stroke-button-button-cta-default, #fdfdfd)';
const tone_cta_disabledBg = 'var(--fill-button-button-cta-disabled, rgba(0,0,0,0.2))';
const tone_cta_disabledText = 'var(--stroke-button-button-cta-disabled, #565656)';
const tone_cta_disabledBorder = 'var(--stroke-button-button-cta-disabled-border, #2a2a2a)';
const tone_cta_outlineBorder = 'var(--stroke-button-button-cta-stroke, #fdfdfd)';

const tone_basic_bg = 'var(--fill-button-button-basic-default, #1c1c1c)';
const tone_basic_text = 'var(--stroke-button-button-basic-default, #fdfdfd)';
const tone_basic_border = 'var(--stroke-border-border-dim, #2a2a2a)';
const tone_basic_disabledBg = 'var(--fill-button-button-basic-disable, rgba(0,0,0,0.2))';
const tone_basic_disabledText = 'var(--stroke-button-button-basic-disabled, #565656)';

const tone_options_bg = 'var(--fill-button-button-options-default, #565656)';
const tone_options_hoverBg = 'var(--fill-button-button-options-hover, #868686)';
const tone_options_text = 'var(--stroke-button-button-options-default, #fdfdfd)';
const tone_options_border = 'var(--stroke-button-button-options-default-border, #0f0f0f)';
const tone_options_disabledBg = 'var(--fill-button-button-options-disable, rgba(0,0,0,0.1))';
const tone_options_disabledText = 'var(--stroke-button-button-options-disabled, #565656)';

const ctaHoverShadow = 'var(--shadow-cta-hover, 0px 0px 7.5px rgba(255,255,255,0.2))';

interface ToneStyle {
  background: string;
  color: string;
  border?: string;
  hoverBackground?: string;
  hoverShadow?: string;
  disabledBackground: string;
  disabledColor: string;
  disabledOpacity?: number;
  // Hairline drawn inside the disabled button so it still reads as a button
  // on the near-black modal surfaces.
  disabledShadow?: string;
  fontWeight: number;
}

const toneStyles = (tone: DosenButtonTone, styleVariant: DosenButtonStyle): ToneStyle => {
  if (tone === 'cta') {
    if (styleVariant === 'outline') {
      return {
        background: 'transparent',
        color: tone_cta_text,
        border: `1px solid ${tone_cta_outlineBorder}`,
        hoverBackground: 'transparent',
        disabledBackground: 'transparent',
        disabledColor: tone_cta_disabledText,
        fontWeight: 600
      };
    }
    return {
      background: tone_cta_bg,
      color: tone_cta_text,
      hoverBackground: tone_cta_bg,
      hoverShadow: ctaHoverShadow,
      disabledBackground: tone_cta_disabledBg,
      disabledColor: tone_cta_disabledText,
      disabledShadow: `inset 0 0 0 1px ${tone_cta_disabledBorder}`,
      fontWeight: 600
    };
  }
  if (tone === 'basic') {
    return {
      background: tone_basic_bg,
      color: tone_basic_text,
      border: `1px solid ${tone_basic_border}`,
      hoverBackground: tone_basic_bg,
      disabledBackground: tone_basic_disabledBg,
      disabledColor: tone_basic_disabledText,
      fontWeight: 500
    };
  }
  // options (Figma "Default")
  return {
    background: tone_options_bg,
    color: tone_options_text,
    border: `1px solid ${tone_options_border}`,
    hoverBackground: tone_options_hoverBg,
    disabledBackground: tone_options_disabledBg,
    disabledColor: tone_options_disabledText,
    fontWeight: 500
  };
};

const DosenButton: React.FC<DosenButtonProps> = ({
  children,
  sizeVariant = 'lg',
  // Accepted for older callers but never drawn: text buttons carry no icon.
  iconPosition: _iconPosition,
  icon: _icon,
  tone = 'cta',
  styleVariant = 'filled',
  iconOnly = false,
  locked = false,
  loading = false,
  loadingText,
  sx,
  disabled,
  ...rest
}) => {
  const sizeKey = sizeAlias(sizeVariant);
  const paddingVariant = paddingMap[sizeKey].none;
  const font = fontMap[sizeKey];
  const styles = toneStyles(tone, styleVariant);

  const iconOnlyPadding = '5px';
  const iconOnlyRadius = 17;
  const isDisabled = loading || disabled || locked;

  return (
    <Button
      {...rest}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      sx={{
        textTransform: 'none',
        background: styles.background,
        color: styles.color,
        border: styles.border ?? 'none',
        fontFamily: 'Inter, sans-serif',
        fontWeight: styles.fontWeight,
        fontSize: iconOnly ? undefined : font.fontSize,
        lineHeight: iconOnly ? undefined : font.lineHeight,
        borderRadius: iconOnly ? `${iconOnlyRadius}px` : `${font.borderRadius}px`,
        padding: iconOnly ? iconOnlyPadding : paddingVariant,
        gap: iconOnly ? '0px' : `${font.gap}px`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: 'unset',
        minWidth: iconOnly ? 'auto' : undefined,
        boxShadow: 'none',
        '&:hover': {
          background: styles.hoverBackground ?? styles.background,
          boxShadow: styles.hoverShadow ?? 'none',
          opacity: styles.hoverShadow ? 1 : 0.95
        },
        '&:disabled': {
          background: styles.disabledBackground,
          color: styles.disabledColor,
          opacity: styles.disabledOpacity ?? 1,
          boxShadow: styles.disabledShadow ?? 'none',
          border: styles.border ? `1px solid ${tone_basic_border}` : undefined,
          cursor: locked ? 'not-allowed' : loading ? 'progress' : 'default'
        },
        ...sx
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'inherit',
          opacity: locked ? 0.35 : 1
        }}
      >
        {loading ? loadingText ?? children : children}
      </Box>
      {locked && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            color: 'inherit',
            zIndex: 1
          }}
        >
          <LockOutlinedIcon
            sx={{ fontSize: 26, color: 'var(--text-text-primary, #fdfdfd)', opacity: 0.65 }}
          />
        </Box>
      )}
    </Button>
  );
};

export default DosenButton;
